
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Upload, RefreshCw, AlertCircle, CheckCircle, Save, Search, CreditCard, Calendar, Hash, Building2 } from 'lucide-react';
import { readCsvFile, parseCsv } from '../../utils/csv';
import { readPdfText, readPdfTextWithY, parseUnionPdfLine, parseMinamisatsumaUnionPdf, resetPdfParserState } from '../../utils/pdf';
import { api, normalizeForComparison, normalizeStringSimple } from '../../services/api';
import { Client, BillingRecord, DensaiReservation, UnionPaymentTerm } from '../../types';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { getDb } from '../../lib/firebase';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  type: 'billing' | 'bank' | 'client' | 'union' | 'densai'; 
  onSuccess: (importedMonth?: string) => void;
}

const UploadModal: React.FC<Props> = ({ isOpen, onClose, type, onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [encoding, setEncoding] = useState<'shift-jis' | 'utf-8'>('shift-jis');
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [successInfo, setSuccessInfo] = useState<string | null>(null);
  const [unionType, setUnionType] = useState<'satsuma' | 'minamisatsuma'>('satsuma');
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [targetMonth, setTargetMonth] = useState('');
  const [monthBillingRecords, setMonthBillingRecords] = useState<BillingRecord[]>([]);
  // 組合明細用：1〜3ヶ月前の請求データをまとめて保持（payment_cycle対応）
  const [billingByMonth, setBillingByMonth] = useState<Record<string, BillingRecord[]>>({});
  const [unionPaymentTermsMap, setUnionPaymentTermsMap] = useState<Record<string, number>>({}); // client_code -> payment_cycle

  // でんさい手動登録用ステート
  const [densaiForm, setDensaiForm] = useState({
    record_number: '',
    densai_user_code: '',
    payment_date: '',
    amount: '',
    remitter_name: '', 
    client_code: '',   
  });
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [updateMaster, setUpdateMaster] = useState(true);

  useEffect(() => {
    if (isOpen) {
        setFile(null);
        setPreviewData([]);
        setHeaders([]);
        setError(null);
        setSuccessInfo(null);
        setIsProcessing(false);
        setEncoding('shift-jis'); 
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        setTargetMonth(`${yyyy}-${mm}`);
        
        setDensaiForm({
            record_number: '',
            densai_user_code: '',
            payment_date: `${yyyy}-${mm}-${dd}`,
            amount: '',
            remitter_name: '',
            client_code: '',
        });
        setUpdateMaster(true);

        api.clients.getAll().then(setAllClients);
    }
  }, [isOpen, type]); // type変更時もリセット

  const fetchMonthBilling = useCallback(async () => {
    if (!targetMonth) return;
    try {
        const db = getDb();
        const baseMonth = targetMonth.replace('-', '/');
        const [y, m] = baseMonth.split('/').map(Number);

        if (type === 'union') {
            // 翌月・翌々月・翌々々月払いに対応するため、1〜3ヶ月前の請求データをまとめて取得
            const monthsToFetch: string[] = [];
            for (let offset = 1; offset <= 3; offset++) {
                const d = new Date(y, m - 1 - offset, 1);
                monthsToFetch.push(`${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}`);
            }

            const newBillingByMonth: Record<string, BillingRecord[]> = {};
            await Promise.all(monthsToFetch.map(async (qm) => {
                const qmAlt = qm.replace('/', '-');
                const [snap1, snap2] = await Promise.all([
                    getDocs(query(collection(db, 'billing_records'), where('import_month', '==', qm))),
                    getDocs(query(collection(db, 'billing_records'), where('import_month', '==', qmAlt)))
                ]);
                const records: BillingRecord[] = [];
                const seenIds = new Set<string>();
                [snap1, snap2].forEach(snap => {
                    snap.forEach(d => {
                        if (!seenIds.has(d.id)) {
                            seenIds.add(d.id);
                            records.push(d.data() as BillingRecord);
                        }
                    });
                });
                newBillingByMonth[qm] = records;
                console.log(`[UploadModal] Union billing for ${qm}: ${records.length} records`);
            }));
            setBillingByMonth(newBillingByMonth);

            // union_payment_terms を取得し、client_code -> payment_cycle のマップを構築
            const termsSnap = await getDocs(collection(db, 'union_payment_terms'));
            const termsMap: Record<string, number> = {};
            termsSnap.forEach(d => {
                const data = d.data() as UnionPaymentTerm;
                if (data.client_code && data.payment_cycle) {
                    termsMap[data.client_code.trim()] = data.payment_cycle;
                }
            });
            setUnionPaymentTermsMap(termsMap);
            console.log(`[UploadModal] Union payment terms loaded: ${Object.keys(termsMap).length} entries`);
        } else {
            // 非組合タイプは targetMonth の請求データをそのまま使用
            const queryMonthAlt = baseMonth.replace('/', '-');
            const [snap1, snap2] = await Promise.all([
                getDocs(query(collection(db, 'billing_records'), where('import_month', '==', baseMonth))),
                getDocs(query(collection(db, 'billing_records'), where('import_month', '==', queryMonthAlt)))
            ]);
            const records: BillingRecord[] = [];
            const seenIds = new Set<string>();
            [snap1, snap2].forEach(snap => {
                snap.forEach(d => {
                    if (!seenIds.has(d.id)) {
                        seenIds.add(d.id);
                        records.push(d.data() as BillingRecord);
                    }
                });
            });
            setMonthBillingRecords(records);
        }
    } catch (e) {
        console.error("Failed to fetch billing records for lookup", e);
    }
  }, [targetMonth, type]);

  useEffect(() => {
      if (isOpen && targetMonth && type !== 'densai') fetchMonthBilling();
  }, [isOpen, targetMonth, fetchMonthBilling, type]);

  const cleanNumber = (val: any): number => {
    if (typeof val === 'number') return val;
    if (!val) return 0;
    const cleaned = String(val).replace(/[¥,円\s]/g, '').replace(/[▲△－ー\-]/g, '-'); 
    return parseInt(cleaned) || 0;
  };

  const formatBankDate = (raw: string, currentTargetMonth: string): string => {
    if (!raw) return '';
    const d = String(raw).replace(/\D/g, ''); 
    const [yearPart, monthPart] = currentTargetMonth.split('-');
    
    if (d.length === 5) {
      const g = parseInt(d.substring(0, 1)); 
      const year = 2018 + g; 
      const month = d.substring(1, 3);
      const day = d.substring(3, 5);
      return `${year}/${month}/${day}`;
    }
    if (d.length === 4) return `${yearPart}/${d.substring(0, 2)}/${d.substring(2, 4)}`;
    if (d.length === 6) {
      const yy = parseInt(d.substring(0, 2));
      const year = yy >= 70 ? 1900 + yy : 2000 + yy;
      return `${year}/${d.substring(2, 4)}/${d.substring(4, 6)}`;
    }
    if (d.length === 8) return `${d.substring(0, 4)}/${d.substring(4, 6)}/${d.substring(6, 8)}`;
    if (d.length >= 1 && d.length <= 2) return `${yearPart}/${monthPart}/${d.padStart(2, '0')}`;
    return raw.replace(/-/g, '/');
  };

  const processFile = useCallback(async (selectedFile: File, currentEncoding: 'shift-jis' | 'utf-8') => {
    setIsProcessing(true);
    setProgressMessage("ファイルを解析しています...");
    setError(null);
    try {
      const isPdf = selectedFile.name.toLowerCase().endsWith('.pdf');
      let parsed: any[] = [];
      
      if (isPdf) {
          // PDF Processing
          resetPdfParserState(); 
          const lines = await readPdfText(selectedFile);

          if (type === 'union') {
              let rawLines: any[];
              if (unionType === 'minamisatsuma') {
                  const linesWithY = await readPdfTextWithY(selectedFile);
                  rawLines = parseMinamisatsumaUnionPdf(linesWithY);
              } else {
                  rawLines = lines.map(line => parseUnionPdfLine(line)).filter(r => r !== null);
              }
              setHeaders(['得意先CD', '名称/振込名', '区分', '出荷', '請求額', '入金額']);
              
              const targetPrefix = unionType === 'satsuma' ? '88' : '83';

              // payment_cycleに応じた月の請求データから得意先の請求額を取得するヘルパー
              const getUnionBillingRecords = (clientCode: string | null): BillingRecord[] => {
                  const [y2, m2] = targetMonth.replace('-', '/').split('/').map(Number);
                  const cycle = clientCode ? (unionPaymentTermsMap[clientCode.trim()] ?? 1) : 1;
                  const d2 = new Date(y2, m2 - 1 - cycle, 1);
                  const billingMonth = `${d2.getFullYear()}/${String(d2.getMonth() + 1).padStart(2, '0')}`;
                  return billingByMonth[billingMonth] || [];
              };

              const findMatchingClients = (clients: Client[], normName: string, rawName: string) => {
                  return clients.filter(c => {
                      const mName = normalizeForComparison(c.client_name);
                      const mAbbr = normalizeForComparison(c.client_abbreviation || '');
                      const mKana = normalizeForComparison(c.client_kana || '');
                      const importKeywords = (c.imported_name || '').split(/[,、\s　]+/).map(k => normalizeForComparison(k)).filter(k => k);
                      const matchImport = importKeywords.some(k => normName.includes(k) || k.includes(normName));
                      const simpleRawName = normalizeStringSimple(rawName);
                      const simpleImportKeywords = (c.imported_name || '').split(/[,、\s　]+/).map(k => normalizeStringSimple(k)).filter(k => k);
                      const matchSimpleImport = simpleImportKeywords.some(k => simpleRawName.includes(k) || k.includes(simpleRawName));
                      return (mName && (normName.includes(mName) || mName.includes(normName))) ||
                             (mAbbr && (normName.includes(mAbbr) || mAbbr.includes(normName))) ||
                             (mKana && (normName.includes(mKana) || mKana.includes(normName))) ||
                             matchImport || matchSimpleImport;
                  });
              };

              parsed = rawLines.map(raw => {
                  // 1. 比較用正規化（法人格なし）で広範囲に検索
                  const normRawName = normalizeForComparison(raw.client_abbreviation);

                  // まず組合コード(88/83)のクライアントで検索、見つからなければ全クライアントで検索
                  const prefixClients = allClients.filter(c => c.client_code.startsWith(targetPrefix));
                  let potentialClients = findMatchingClients(prefixClients, normRawName, raw.client_abbreviation);
                  if (potentialClients.length === 0) {
                      potentialClients = findMatchingClients(allClients, normRawName, raw.client_abbreviation);
                  }

                  let matchedClient: Client | null = null;

                  if (potentialClients.length === 1) {
                      // 候補が1つなら即決
                      matchedClient = potentialClients[0];
                  } else if (potentialClients.length > 1) {
                      // 2. 複数候補がある場合のみ、法人格の位置を含めた厳密な判定を行う
                      const simpleRawName = normalizeStringSimple(raw.client_abbreviation);

                      let bestScore = -1;

                      // 同スコア時のタイブレーカーを安定化するため、得意先コード昇順でソート
                      // （Firestoreの返却順に依存して両環境で違う得意先が選ばれる非決定性を防ぐ）
                      const sortedCandidates = [...potentialClients].sort((a, b) =>
                          (a.client_code || '').localeCompare(b.client_code || '')
                      );

                      sortedCandidates.forEach(client => {
                          let score = 0;
                          const simpleClientName = normalizeStringSimple(client.client_name);
                          const simpleAbbr = normalizeStringSimple(client.client_abbreviation || '');
                          const simpleKana = normalizeStringSimple(client.client_kana || '');
                          
                          const importKeywords = (client.imported_name || '').split(/[,、\s　]+/).map(k => normalizeStringSimple(k)).filter(k => k);
                          
                          // A. 法人格を含めた名称の一致度判定
                          if (simpleClientName === simpleRawName) {
                              score += 100; // 完全一致
                          } else if (importKeywords.includes(simpleRawName)) {
                              score += 100; // 照合用キーワード完全一致は最優先
                          } else if (simpleAbbr === simpleRawName) {
                              score += 90;
                          } else if (simpleKana === simpleRawName) {
                              score += 80;
                          } else {
                              // A'. 法人格除去後の完全一致（同名異法人格の誤マッチ防止）
                              const normClientName = normalizeForComparison(client.client_name);
                              const normClientAbbr = normalizeForComparison(client.client_abbreviation || '');
                              const normClientKana = normalizeForComparison(client.client_kana || '');
                              if (normClientName === normRawName || normClientAbbr === normRawName || normClientKana === normRawName) {
                                  score += 75;
                              } else if (simpleClientName.includes(simpleRawName) || simpleRawName.includes(simpleClientName)) {
                                  // 部分一致
                                  score += 20;
                              } else if (importKeywords.some(k => simpleRawName.includes(k) || k.includes(simpleRawName))) {
                                  score += 20;
                              }
                          }

                          // B. 請求実績による判定（union型はbillingByMonth全月で確認）
                          const allBillingRecs = type === 'union'
                              ? Object.values(billingByMonth).flat()
                              : monthBillingRecords;
                          const hasBilling = allBillingRecs.some(b => b.client_code?.trim() === client.client_code?.trim());
                          if (hasBilling) {
                              score += 50;
                          }

                          // B'. 請求額と入金額の一致判定（同名・別コード得意先の決定打）
                          // payment_cycleに応じた月の請求額が入金額と一致する得意先を優先
                          if (type === 'union' && raw.deposit_amount > 0) {
                              const cycleBillings = getUnionBillingRecords(client.client_code);
                              const candidateBilling = cycleBillings.find(b => b.client_code?.trim() === client.client_code?.trim());
                              if (candidateBilling) {
                                  const billingDiff = Math.abs((candidateBilling.billing_balance || 0) - raw.deposit_amount);
                                  if (billingDiff === 0) {
                                      score += 30; // 請求額完全一致（決定打）
                                  } else if (billingDiff <= 1000) {
                                      score += 20; // 手数料許容範囲
                                  }
                              }
                          }

                          if (score > bestScore) {
                              bestScore = score;
                              matchedClient = client;
                          }
                      });
                  }

                  // フォールバック: それでも決まらなければ候補の先頭
                  if (!matchedClient && potentialClients.length > 0) {
                      matchedClient = potentialClients[0];
                  }

                  // payment_cycleに応じた月の請求データを参照
                  const matchedCode = matchedClient ? matchedClient.client_code : null;
                  const billingRecs = getUnionBillingRecords(matchedCode);
                  const billingMatch = matchedCode
                      ? billingRecs.find(b => b.client_code?.trim() === matchedCode.trim())
                      : billingRecs.find(b => {
                          if (!b.client_code.startsWith(targetPrefix)) return false;
                          const bk = normalizeForComparison(b.client_kana);
                          return bk && (bk.includes(normRawName) || normRawName.includes(bk));
                      });

                  const billingAmount = billingMatch ? billingMatch.billing_balance : 0;
                  const depositAmount = raw.deposit_amount || 0;

                  return {
                      client_code: matchedClient ? matchedClient.client_code : '未特定',
                      client_name: matchedClient ? matchedClient.client_name : raw.client_abbreviation,
                      category: "-",
                      shipping: "-",
                      billing_amount: billingAmount,
                      deposit_amount: depositAmount,
                      difference: billingAmount - depositAmount,
                      deposit_date: raw.deposit_date
                  };
              });
          } else {
              throw new Error("このPDF形式には対応していません。");
          }

      } else {
          // CSV Processing
          const text = await readCsvFile(selectedFile, currentEncoding);
          const rows = parseCsv(text);
          if (rows.length === 0) throw new Error("ファイルが空です。");
          
          if (type === 'client') {
              setHeaders(['得意先CD', '得意先名', '略称', 'カナ']);
              parsed = rows.slice(1).map(row => {
                  if (!row || row.length < 2) return null;
                  return { client_code: row[0]?.trim(), client_name: row[1]?.trim(), client_abbreviation: row[2]?.trim(), client_kana: row[3]?.trim() };
              }).filter(r => r && r.client_code);
          } else if (type === 'billing') {
              setHeaders(['得意先CD', '得意先名', '請求額', '対象月']);
              // ヘッダー行から金額カラムを自動検出
              const headerRow = rows[0] || [];
              let amountColIdx = -1;
              const amountKeywords = ['請求残高', '残高', '請求額', '金額', '請求金額', '売掛残高'];
              for (let ci = 0; ci < headerRow.length; ci++) {
                  const h = (headerRow[ci] || '').trim();
                  if (amountKeywords.some(k => h.includes(k))) {
                      amountColIdx = ci;
                      break;
                  }
              }
              // ヘッダーで見つからない場合はフォールバック
              if (amountColIdx === -1) {
                  amountColIdx = headerRow.length > 6 ? 6 : 2;
              }
              console.log(`[Billing Import] Amount column index: ${amountColIdx} (header: "${headerRow[amountColIdx] || 'N/A'}")`);

              // 得意先CDカラムも自動検出
              let codeColIdx = 0;
              let nameColIdx = 1;
              const codeKeywords = ['得意先CD', '得意先コード', '顧客コード', '顧客CD', 'コード', 'CD'];
              const nameKeywords = ['得意先名', '顧客名', '名称', '得意先'];
              for (let ci = 0; ci < headerRow.length; ci++) {
                  const h = (headerRow[ci] || '').trim();
                  if (codeKeywords.some(k => h.includes(k))) codeColIdx = ci;
                  if (nameKeywords.some(k => h === k || (h.includes(k) && !h.includes('CD') && !h.includes('コード')))) nameColIdx = ci;
              }

              parsed = rows.slice(1).map(row => {
                  const code = row[codeColIdx]?.trim();
                  if (!code) return null;
                  const amount = cleanNumber(row[amountColIdx]);
                  return { client_code: code, client_name: row[nameColIdx]?.trim() || '-', billing_balance: amount, import_month: targetMonth.replace('-', '/') };
              }).filter(r => r !== null);
          } else if (type === 'bank') {
              setHeaders(['日付', '振込人名', '金額']);
              parsed = rows.slice(1).map(row => {
                  if (row.length < 8) return null;
                  const rawDate = row[2]?.trim();
                  const remitter = row[7]?.trim();
                  const amount = cleanNumber(row[4]);
                  if (!rawDate || !remitter || amount === 0) return null;
                  return { deposit_date: formatBankDate(rawDate, targetMonth), remitter_name: remitter, amount: amount };
              }).filter(r => r !== null);
          } else if (type === 'union') {
              setHeaders(['得意先CD', '名称/振込名', '区分', '出荷', '請求額', '入金額']);
              const targetPrefix = unionType === 'satsuma' ? '88' : '83';

              // payment_cycleに応じた月の請求データから得意先の請求額を取得するヘルパー（CSV版）
              const getUnionBillingRecordsCSV = (clientCode: string | null): BillingRecord[] => {
                  const [y2, m2] = targetMonth.replace('-', '/').split('/').map(Number);
                  const cycle = clientCode ? (unionPaymentTermsMap[clientCode.trim()] ?? 1) : 1;
                  const d2 = new Date(y2, m2 - 1 - cycle, 1);
                  const billingMonth = `${d2.getFullYear()}/${String(d2.getMonth() + 1).padStart(2, '0')}`;
                  return billingByMonth[billingMonth] || [];
              };

              const findMatchingClientsCSV = (clients: Client[], normName: string, rawName: string) => {
                  return clients.filter(c => {
                      const mName = normalizeForComparison(c.client_name);
                      const mAbbr = normalizeForComparison(c.client_abbreviation || '');
                      const mKana = normalizeForComparison(c.client_kana || '');
                      const importKeywords = (c.imported_name || '').split(/[,、\s　]+/).map(k => normalizeForComparison(k)).filter(k => k);
                      const matchImport = importKeywords.some(k => normName.includes(k) || k.includes(normName));
                      const simpleName = normalizeStringSimple(rawName);
                      const simpleImportKeywords = (c.imported_name || '').split(/[,、\s　]+/).map(k => normalizeStringSimple(k)).filter(k => k);
                      const matchSimpleImport = simpleImportKeywords.some(k => simpleName.includes(k) || k.includes(simpleName));
                      return (mName && (normName.includes(mName) || mName.includes(normName))) ||
                             (mAbbr && (normName.includes(mAbbr) || mAbbr.includes(normName))) ||
                             (mKana && (normName.includes(mKana) || mKana.includes(normName))) ||
                             matchImport || matchSimpleImport;
                  });
              };

              parsed = rows.map(row => {
                  if (!row || row.length < 2) return null;

                  const dateVal = row[0]?.trim();
                  const nameFromFile = row[1]?.trim();
                  const rawDeposit = row[2];

                  const isHeader = /[^\d,\-¥円\s.]/.test(String(rawDeposit).trim());

                  if (!dateVal || !nameFromFile) return null;
                  if (nameFromFile === '名称' || nameFromFile.includes('振込名')) return null;
                  if (isHeader) return null;

                  const normName = normalizeForComparison(nameFromFile);

                  // まず組合コード(88/83)のクライアントで検索、見つからなければ全クライアントで検索
                  const prefixClients = allClients.filter(c => c.client_code.startsWith(targetPrefix));
                  let potentialClients = findMatchingClientsCSV(prefixClients, normName, nameFromFile);
                  if (potentialClients.length === 0) {
                      potentialClients = findMatchingClientsCSV(allClients, normName, nameFromFile);
                  }

                  let matchedClient: Client | null = null;

                  // 入金額（cleanNumber）を事前に算出しておく（マッチング判定で使用）
                  const depositAmount = cleanNumber(rawDeposit);

                  if (potentialClients.length === 1) {
                      matchedClient = potentialClients[0];
                  } else if (potentialClients.length > 1) {
                      const simpleName = normalizeStringSimple(nameFromFile);
                      let bestScore = -1;

                      // 同スコア時のタイブレーカーを安定化するため、得意先コード昇順でソート
                      // （Firestoreの返却順に依存して両環境で違う得意先が選ばれる非決定性を防ぐ）
                      const sortedCandidates = [...potentialClients].sort((a, b) =>
                          (a.client_code || '').localeCompare(b.client_code || '')
                      );

                      sortedCandidates.forEach(client => {
                          let score = 0;
                          const simpleClientName = normalizeStringSimple(client.client_name);
                          const simpleAbbr = normalizeStringSimple(client.client_abbreviation || '');
                          const simpleKana = normalizeStringSimple(client.client_kana || '');
                          
                          const importKeywords = (client.imported_name || '').split(/[,、\s　]+/).map(k => normalizeStringSimple(k)).filter(k => k);
                          
                          if (simpleClientName === simpleName) {
                              score += 100;
                          } else if (importKeywords.includes(simpleName)) {
                              score += 100; // 照合用キーワード完全一致は最優先
                          } else if (simpleAbbr === simpleName) {
                              score += 90;
                          } else if (simpleKana === simpleName) {
                              score += 80;
                          } else {
                              // 法人格除去後の完全一致（同名異法人格の誤マッチ防止）
                              const normClientName = normalizeForComparison(client.client_name);
                              const normClientAbbr = normalizeForComparison(client.client_abbreviation || '');
                              const normClientKana = normalizeForComparison(client.client_kana || '');
                              if (normClientName === normName || normClientAbbr === normName || normClientKana === normName) {
                                  score += 75;
                              } else if (simpleClientName.includes(simpleName) || simpleName.includes(simpleClientName)) {
                                  score += 20;
                              } else if (importKeywords.some(k => simpleName.includes(k) || k.includes(simpleName))) {
                                  score += 20;
                              }
                          }

                          // union型はbillingByMonth全月で請求実績を確認
                          const allBillingRecsCSV = type === 'union'
                              ? Object.values(billingByMonth).flat()
                              : monthBillingRecords;
                          const hasBilling = allBillingRecsCSV.some(b => b.client_code?.trim() === client.client_code?.trim());
                          if (hasBilling) {
                              score += 50;
                          }

                          // B'. 請求額と入金額の一致判定（同名・別コード得意先の決定打）
                          // payment_cycleに応じた月の請求額が入金額と一致する得意先を優先
                          if (type === 'union' && depositAmount > 0) {
                              const cycleBillings = getUnionBillingRecordsCSV(client.client_code);
                              const candidateBilling = cycleBillings.find(b => b.client_code?.trim() === client.client_code?.trim());
                              if (candidateBilling) {
                                  const billingDiff = Math.abs((candidateBilling.billing_balance || 0) - depositAmount);
                                  if (billingDiff === 0) {
                                      score += 30; // 請求額完全一致（決定打）
                                  } else if (billingDiff <= 1000) {
                                      score += 20; // 手数料許容範囲
                                  }
                              }
                          }

                          if (score > bestScore) {
                              bestScore = score;
                              matchedClient = client;
                          }
                      });
                  }

                  if (!matchedClient && potentialClients.length > 0) {
                      matchedClient = potentialClients[0];
                  }

                  // payment_cycleに応じた月の請求データを参照
                  const matchedCodeCSV = matchedClient ? matchedClient.client_code : null;
                  const billingRecsCSV = getUnionBillingRecordsCSV(matchedCodeCSV);
                  const billingMatch = matchedCodeCSV
                      ? billingRecsCSV.find(b => b.client_code?.trim() === matchedCodeCSV.trim())
                      : billingRecsCSV.find(b => {
                          if (!b.client_code.startsWith(targetPrefix)) return false;
                          const bk = normalizeForComparison(b.client_kana);
                          return bk && (bk.includes(normName) || normName.includes(bk));
                      });

                  const billingAmount = billingMatch ? billingMatch.billing_balance : 0;

                  return {
                      client_code: matchedClient ? matchedClient.client_code : '未特定', 
                      client_name: matchedClient ? matchedClient.client_name : nameFromFile, 
                      category: "-", 
                      shipping: "-", 
                      billing_amount: billingAmount, 
                      deposit_amount: depositAmount, 
                      difference: billingAmount - depositAmount, 
                      deposit_date: dateVal 
                  };
              }).filter(r => r !== null);
          }
      }
      setPreviewData(parsed);
      if (parsed.length === 0) setError("有効なデータを抽出できませんでした。");
    } catch(e:any) { setError(e.message); setPreviewData([]); }
    finally { 
        setIsProcessing(false); 
    }
  }, [allClients, type, targetMonth, monthBillingRecords, billingByMonth, unionPaymentTermsMap, unionType]);

  useEffect(() => { if (file) processFile(file, encoding); }, [encoding, file, processFile]);

  const handleClientSelect = (client: Client) => {
    setDensaiForm(prev => ({
        ...prev,
        client_code: client.client_code,
        remitter_name: client.client_name,
        densai_user_code: client.densai_user_code || prev.densai_user_code
    }));
  };

  const executeRegister = async () => {
      setIsProcessing(true);
      setError(null);
      setProgressMessage("データ処理中.."); 
      
      try {
          if (type === 'densai') {
              if (!densaiForm.record_number || !densaiForm.amount || !densaiForm.payment_date || !densaiForm.remitter_name) {
                  throw new Error("必須項目が入力されていません。");
              }
              if (densaiForm.record_number.length !== 20) {
                  throw new Error("記録番号は20桁である必要があります。");
              }
              const exists = await api.densai.checkByRecordNumber(densaiForm.record_number);
              if (exists) {
                  throw new Error(`記録番号 ${densaiForm.record_number} は既に登録されています。`);
              }
              if (updateMaster && densaiForm.client_code && densaiForm.densai_user_code) {
                  const client = allClients.find(c => c.client_code === densaiForm.client_code);
                  if (client && client.densai_user_code !== densaiForm.densai_user_code) {
                      await api.clients.save({ ...client, densai_user_code: densaiForm.densai_user_code });
                  }
              }
              
              // 登録処理: 予約登録のみ行う (pending状態)
              await api.densai.registerManualDensai({
                  id: '',
                  client_code: densaiForm.client_code,
                  client_name: allClients.find(c => c.client_code === densaiForm.client_code)?.client_name || densaiForm.remitter_name,
                  densai_user_code: densaiForm.densai_user_code,
                  remitter_name_pdf: densaiForm.remitter_name,
                  payment_date: densaiForm.payment_date.replace(/-/g, '/'),
                  amount: Number(densaiForm.amount),
                  record_number: densaiForm.record_number,
                  status: 'pending', // Pending状態で登録
                  created_at: '' // api側で設定
              } as DensaiReservation);
              
              setSuccessInfo('でんさい発生記録を登録しました。（入金待ち）');
              onSuccess();
              setTimeout(onClose, 1500);
              return;
          }

          if (previewData.length === 0) throw new Error("登録可能なデータがありません。");
          let res: { imported: number, skipped: number } = { imported: 0, skipped: 0 };
          
          if (type === 'client') res = await api.clients.importBatch(previewData);
          else if (type === 'union') {
              await api.union.createBatch(unionType, targetMonth.replace('-', '/'), previewData);
              res = { imported: previewData.length, skipped: 0 };
          }
          else if (type === 'billing') res = await api.clients.importBillingBatch(previewData);
          else if (type === 'bank') res = await api.reconciliation.importBankData(previewData);
          
          setSuccessInfo(`${res.imported}件を登録しました。`);
          onSuccess(targetMonth.replace('-', '/'));
          setTimeout(onClose, 1500);
      } catch(e:any) { 
          setError(e.message); 
      } finally {
          setIsProcessing(false);
      }
  };

  const getDisplayKeys = () => {
      if (type === 'union') return ['client_code', 'client_name', 'category', 'shipping', 'billing_amount', 'deposit_amount'];
      if (type === 'client') return ['client_code', 'client_name', 'client_abbreviation', 'client_kana'];
      if (type === 'billing') return ['client_code', 'client_name', 'billing_balance', 'import_month'];
      if (type === 'bank') return ['deposit_date', 'remitter_name', 'amount'];
      return [];
  };

  const getUploadConfig = () => {
    switch (type) {
      case 'union':
        return { message: 'CSVまたはPDFを選択してください', accept: '.csv,.pdf' };
      default:
        return { message: 'CSVを選択してください', accept: '.csv' };
    }
  };

  const uploadConfig = getUploadConfig();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-xs">
      <div className={`bg-white rounded shadow-2xl w-full p-0 flex flex-col max-h-[90vh] overflow-hidden animate-in fade-in zoom-in duration-200 ${type === 'densai' ? 'max-w-2xl' : 'max-w-4xl'}`}>
        <div className="px-5 py-3 border-b flex justify-between items-center bg-gray-50">
            <h2 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                {type === 'densai' ? <CreditCard size={18} className="text-teal-600"/> : <Upload size={18} />}
                {type === 'bank' ? '銀行FBデータ取込' : type === 'billing' ? '請求データ取込' : type === 'client' ? '得意先マスタ取込' : type === 'densai' ? 'でんさい発生記録 手動登録' : '組合明細取込'}
            </h2>
            {!isProcessing && <button onClick={onClose} className="text-gray-400 p-1 hover:text-gray-600"><X size={18} /></button>}
        </div>
        
        {/* Simple Loading Overlay without extra info */}
        {isProcessing ? (
            <div className="p-20 flex flex-col items-center justify-center space-y-6">
                <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full"></div>
                <h3 className="text-lg font-black text-gray-800">{progressMessage}</h3>
            </div>
        ) : type === 'densai' ? (
            /* DENSAI UI */
            <div className="p-6 space-y-5 overflow-y-auto">
                <div className="bg-teal-50 border border-teal-100 p-3 rounded text-[13px] text-teal-800 mb-2 flex items-start gap-2">
                    <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                    <div>
                        <p className="font-bold mb-1">手動登録モード</p>
                        <p className="opacity-90 leading-relaxed">
                            発生記録通知書（PDF）の内容を確認しながら入力してください。<br/>
                            法人名を入力すると得意先マスタを検索・補完できます。
                        </p>
                    </div>
                </div>
                {/* ... densai form content ... */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     {/* 記録番号 */}
                     <div className="col-span-2">
                        <label className="block text-xs font-black text-gray-500 uppercase mb-1 flex items-center gap-1">
                            <Hash size={14} /> 記録番号 (20桁) <span className="text-red-500">*</span>
                        </label>
                        <input 
                            type="text" 
                            maxLength={20}
                            value={densaiForm.record_number}
                            onChange={(e) => setDensaiForm({...densaiForm, record_number: e.target.value.replace(/[^0-9a-zA-Z]/g, '').toUpperCase()})}
                            placeholder="例: 00000000000000000000"
                            className="w-full border border-gray-300 rounded px-3 py-2 font-mono text-sm tracking-widest focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none uppercase"
                        />
                     </div>
                     {/* 支払期日 */}
                     <div>
                        <label className="block text-xs font-black text-gray-500 uppercase mb-1 flex items-center gap-1">
                            <Calendar size={14} /> 支払期日 <span className="text-red-500">*</span>
                        </label>
                        <input 
                            type="date" 
                            value={densaiForm.payment_date}
                            onChange={(e) => setDensaiForm({...densaiForm, payment_date: e.target.value})}
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-bold focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                        />
                     </div>
                     {/* 債権金額 */}
                     <div>
                        <label className="block text-xs font-black text-gray-500 uppercase mb-1 flex items-center gap-1">
                            <span className="text-lg leading-none">¥</span> 債権金額 <span className="text-red-500">*</span>
                        </label>
                        <input 
                            type="number" 
                            value={densaiForm.amount}
                            onChange={(e) => setDensaiForm({...densaiForm, amount: e.target.value})}
                            placeholder="0"
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono text-right focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                        />
                     </div>
                     {/* 法人名 */}
                     <div className="col-span-2 relative">
                        <label className="block text-xs font-black text-gray-500 uppercase mb-1 flex items-center gap-1">
                            <Building2 size={14} /> 法人名 / 得意先名 <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <input 
                                type="text" 
                                value={densaiForm.remitter_name}
                                onChange={(e) => { setDensaiForm({...densaiForm, remitter_name: e.target.value}); setShowClientSuggestions(true); }}
                                onFocus={() => setShowClientSuggestions(true)}
                                placeholder="名称を入力して検索..."
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm pl-9 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none font-bold"
                            />
                            <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                        </div>
                        {showClientSuggestions && densaiForm.remitter_name && (
                            <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 overflow-hidden max-h-48 overflow-y-auto">
                                {allClients.filter(c => {
                                    const term = normalizeForComparison(densaiForm.remitter_name);
                                    const cName = normalizeForComparison(c.client_name);
                                    const cKana = normalizeForComparison(c.client_kana);
                                    return cName.includes(term) || cKana.includes(term);
                                }).slice(0, 8).map(client => (
                                    <button key={client.id} type="button" onClick={() => { handleClientSelect(client); setShowClientSuggestions(false); }} className="w-full text-left px-4 py-2 hover:bg-teal-50 text-[13px] border-b border-gray-50 last:border-0 transition-colors">
                                        <div className="font-bold text-gray-800">{client.client_name}</div>
                                        <div className="flex gap-2 text-[10px] text-gray-500"><span className="font-mono bg-gray-100 px-1 rounded">{client.client_code}</span><span>{client.client_kana}</span></div>
                                    </button>
                                ))}
                            </div>
                        )}
                        {showClientSuggestions && <div className="fixed inset-0 z-0" onClick={() => setShowClientSuggestions(false)} />}
                     </div>
                     {/* 利用者番号 */}
                     <div className="col-span-2 bg-gray-50 p-3 rounded-lg border border-gray-200">
                         <div className="grid grid-cols-2 gap-4">
                             <div className="col-span-2 md:col-span-1">
                                <label className="block text-xs font-black text-gray-500 uppercase mb-1 flex items-center gap-1"><Hash size={14} /> 利用者番号 (9桁) <span className="text-red-500">*</span></label>
                                <input type="text" maxLength={9} value={densaiForm.densai_user_code} onChange={(e) => setDensaiForm({...densaiForm, densai_user_code: e.target.value.replace(/[^0-9a-zA-Z]/g, '').toUpperCase()})} placeholder="例: 00008CPV6" className="w-full border border-gray-300 rounded px-3 py-2 font-mono text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none uppercase bg-white"/>
                             </div>
                             <div className="col-span-2 md:col-span-1 flex items-end">
                                 <label className="flex items-center gap-2 cursor-pointer pb-2">
                                     <input type="checkbox" checked={updateMaster} onChange={(e) => setUpdateMaster(e.target.checked)} className="w-4 h-4 text-teal-600 rounded" disabled={!densaiForm.client_code} />
                                     <span className={`text-xs font-bold ${densaiForm.client_code ? 'text-gray-700' : 'text-gray-400'}`}>得意先マスタの利用者番号も更新する</span>
                                 </label>
                             </div>
                         </div>
                     </div>
                </div>
                {successInfo && <div className="bg-green-50 text-green-700 p-3 rounded border border-green-200 text-[13px] font-black flex items-center gap-2"><CheckCircle size={16} /> {successInfo}</div>}
                {error && <div className="bg-red-50 text-red-700 p-3 rounded border border-red-200 text-[13px] font-black flex items-center gap-2"><AlertCircle size={16} /> {error}</div>}
                <div className="pt-4 flex justify-end gap-3 border-t mt-2">
                    <button onClick={onClose} className="px-6 py-2 rounded border border-gray-300 text-gray-600 font-bold text-sm hover:bg-gray-50 transition-all">キャンセル</button>
                    <button onClick={executeRegister} disabled={isProcessing || !densaiForm.record_number || !densaiForm.amount} className="px-8 py-2 rounded bg-teal-600 text-white font-black text-sm hover:bg-teal-700 disabled:opacity-50 shadow-md transition-all active:scale-95 flex items-center gap-2">{isProcessing ? <RefreshCw size={16} className="animate-spin"/> : <Save size={16}/>} 登録する</button>
                </div>
            </div>
        ) : (
            /* STANDARD UPLOAD UI (Simplified Loading) */
            <>
                <div className="flex-1 overflow-hidden flex flex-col p-5 space-y-4">
                    {(type === 'union' || type === 'billing' || type === 'bank') && (
                        <div className="flex items-center gap-6 p-3 bg-gray-50 rounded border text-[13px]">
                            {type === 'union' && (
                                <div className="flex items-center gap-4">
                                    <label className="flex items-center gap-2 cursor-pointer"><input type="radio" checked={unionType === 'satsuma'} onChange={() => setUnionType('satsuma')} className="w-4 h-4 text-blue-600" /><span className="font-bold text-gray-800">薩摩砕石協同組合</span></label>
                                    <label className="flex items-center gap-2 cursor-pointer"><input type="radio" checked={unionType === 'minamisatsuma'} onChange={() => setUnionType('minamisatsuma')} className="w-4 h-4 text-blue-600" /><span className="font-bold text-gray-800">南さつまリサイクル協同組合</span></label>
                                </div>
                            )}
                            <div className="flex items-center gap-2 ml-auto">
                                <span className="text-xs font-black text-gray-500 uppercase tracking-widest">対象月（日付・請求補完用）：</span>
                                <input type="month" value={targetMonth} onChange={(e) => setTargetMonth(e.target.value)} className="border rounded px-2 py-1 font-bold shadow-sm outline-none focus:ring-1 focus:ring-blue-500 text-gray-800" />
                            </div>
                        </div>
                    )}
                    
                    {!file ? (
                        <div className="flex-1 border-2 border-dashed rounded flex flex-col items-center justify-center p-12 border-gray-200 hover:bg-gray-50 cursor-pointer transition-all" onClick={() => document.getElementById('upload-file')?.click()}>
                            <Upload size={48} className="mb-4 text-blue-500" />
                            <p className="text-gray-400 font-black text-sm uppercase tracking-widest">{uploadConfig.message}</p>
                            <input id="upload-file" type="file" className="hidden" accept={uploadConfig.accept} onChange={(e) => e.target.files?.[0] && setFile(e.target.files[0])} />
                        </div>
                    ) : (
                        // File Preview Table
                        <div className="flex-1 flex flex-col overflow-hidden border rounded bg-white shadow-inner">
                            <div className="bg-gray-50 px-3 py-1.5 border-b flex justify-between items-center text-[11px] text-gray-400 font-bold uppercase tracking-widest">
                                <span>ファイル：{file.name} / 抽出：{previewData.length} 件</span>
                                <button onClick={() => setFile(null)} className="text-blue-600 font-black">ファイル変更</button>
                            </div>
                            <div className="flex-1 overflow-auto scrollbar-thin">
                                <table className="w-full text-[12px] text-left whitespace-nowrap">
                                    <thead className="bg-gray-100 sticky top-0 z-10 shadow-sm">
                                        <tr>{headers.map((h, i) => <th key={i} className="p-3 border-b font-black text-gray-600">{h}</th>)}</tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {previewData.length === 0 ? (
                                            <tr><td colSpan={headers.length || 1} className="p-20 text-center text-gray-300 italic font-bold">抽出可能なデータが見つかりませんでした</td></tr>
                                        ) : previewData.map((row, idx) => (
                                            <tr key={idx} className="hover:bg-blue-50/20">
                                                {getDisplayKeys().map((key, i) => (
                                                    <td key={i} className={`p-3 border-b text-gray-800 font-bold ${key === 'client_code' ? 'font-mono' : ''} ${typeof row[key] === 'number' ? 'text-right' : ''}`}>
                                                        {key === 'client_code' && row[key] === '' ? <span className="text-red-400 italic">未特定</span> : typeof row[key] === 'number' ? row[key].toLocaleString() : (row[key] || '-')}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    
                    <div className="flex items-center gap-4 bg-gray-50 p-2.5 rounded border">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">CSV文字コード：</span>
                        <select value={encoding} onChange={(e) => setEncoding(e.target.value as any)} className="text-[11px] border rounded px-1.5 py-1 font-bold shadow-xs outline-none text-gray-800"><option value="shift-jis">Shift-JIS (推奨)</option><option value="utf-8">UTF-8</option></select>
                        <div className="flex-1 text-right">
                            <span className="text-[10px] text-gray-400 font-bold italic">*名称の部分一致により自動マスタ照合を行っています</span>
                        </div>
                    </div>

                    {successInfo && <div className="bg-green-50 text-green-700 p-3 rounded border border-green-200 text-[13px] font-black flex items-center gap-2 animate-in slide-in-from-bottom-1"><CheckCircle size={16} /> {successInfo}</div>}
                    {error && <div className="bg-red-50 text-red-700 p-3 rounded border border-red-200 text-[13px] font-black flex items-center gap-2"><AlertCircle size={16} /> {error}</div>}
                </div>
                <div className="px-5 py-4 border-t bg-gray-50 flex justify-end gap-3">
                    <button onClick={onClose} className="px-6 py-2 rounded border border-gray-300 text-gray-600 font-black text-[13px] hover:bg-white transition-all">キャンセル</button>
                    <button onClick={executeRegister} disabled={!file || previewData.length === 0 || !!successInfo} className="px-10 py-2 rounded text-white font-black text-[13px] disabled:opacity-50 shadow-md transition-all active:scale-95 bg-blue-600 hover:bg-blue-700">取込を実行</button>
                </div>
            </>
        )}
      </div>
    </div>
  );
};
export default UploadModal;
